import {
  AbpApplicationConfigurationService,
  CoreModule,
  CurrentUserDto,
  ListService,
} from '@abp/ng.core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, signal } from '@angular/core';


import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, firstValueFrom, tap } from 'rxjs';
import { PhoneInputComponent } from 'src/app/standalone/phone-input/phone-input.component';
import { ProfileService, SendEmailConfirmationRequest, SendPhoneConfirmationRequest } from '@proxy/profile';
import { ThemeSharedModule, ToasterService } from '@abp/ng.theme.shared';
import { CommercialUiModule } from '@shared/commercial-ui';
import { PhoneVerificationComponent } from "./phone-confirmation/phone-confirmation.component";

@Component({
    selector: 'app-profile-info',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './profile-info.component.html',
    styleUrls: ['./profile-info.component.scss'],
    imports: [
        CoreModule,
        ThemeSharedModule,
        CommercialUiModule,
        ReactiveFormsModule,
        PhoneInputComponent,
        PhoneVerificationComponent
    ],
    providers: [ListService]
})
export class ProfileInfoComponent {
  public readonly proxyService = inject(ProfileService);
  title = '::profileTabMethods';
  // Signals, not plain fields: these are set in subscribes and after an await, which an OnPush
  // ancestor does not redraw for (#42). The form is rebuilt after each load, so it is one too.
  readonly isLoading = signal(true);
  readonly form = signal<FormGroup | undefined>(undefined);
  selected = {} as any;
  public errorMessage: string;
  protected readonly fb = inject(FormBuilder);
  readonly isConfirmationVisible = signal(false);
  readonly verificationCodeValidity = signal(0);

  constructor(
    private cdr: ChangeDetectorRef,
    private toasterService: ToasterService,
    private appConfigService: AbpApplicationConfigurationService
  ) {
    this.form.set(this.fb.group({
      userName: [null, [Validators.required, Validators.maxLength(50)]],
      name: [null, [Validators.required, Validators.maxLength(50)]],
      email: [null, [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required]],
    }));
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    this.fetchCurrentUser();
  }

  readonly currentUser = signal<CurrentUserDto | undefined>(undefined);

  get phoneNumber() {
    return this.currentUser()?.phoneNumber;
  }

  get isPhoneNumberChanged(){
    return this.currentUser().phoneNumber === this.form().get('phoneNumber').value?.replaceAll(' ', '');
  }

  get verfiyButtonClass(){
    const className = this.currentUser().emailVerified? 'btn-white' : 'btn btn-warning' ;
    return className
  }

  fetchCurrentUser() {
    this.appConfigService.get({ includeLocalizationResources: true }).subscribe({
      next: results => {
        const { userName, name, phoneNumber, email } = results.currentUser;
        this.currentUser.set(results.currentUser);

        this.form.set(this.fb.group({
          userName: [userName ?? null, [Validators.required, Validators.maxLength(50)]],
          name: [name ?? null, [Validators.required, Validators.maxLength(50)]],
          phoneNumber: [phoneNumber ?? null, [Validators.required]],
          email: [email, [Validators.required, Validators.email]],
        }));
        this.isLoading.set(false);
      },
    });
  }

  setLoading(loading: boolean) {
    this.isLoading.set(loading);
  }

  isFormSubmitted = false;
  readonly isBusy = signal(false);
  submitHandler() {
    this.isFormSubmitted = true;
    //this.cdr.markForCheck();
    if (this.form().invalid) return;

    this.isBusy.set(true);

    const request = this.createRequest().pipe(
      finalize(() => this.isBusy.set(false)),
      tap()
    );

    request.subscribe({
      next: () => {
        this.toasterService.success('::SuccessfullyUpdated', '', { closable: false });
        this.isLoading.set(true);
        this.fetchCurrentUser();
      }
    });
  }

  private createRequest() {
    const formValues = {
      ...this.currentUser(),
      ...this.form().value,
      phoneNumber: this.form().value?.phoneNumber?.replaceAll(' ', '')
    };

    return this.proxyService.update(formValues);
  }

  get phoneErrorMessage() {
    const phoneControl = this.form().get('phoneNumber');

    const showError =
      (phoneControl.touched || this.isFormSubmitted) && phoneControl.errors?.required;

    const errorMessage = showError ? this.errorMessage ?? '::InvalidPhoneNumber' : '';
    return errorMessage;
  }

  phoneNumberHandler(phoneNumber: string, isValid: boolean) {
    const phoneControl = this.form().get('phoneNumber');
    this.errorMessage = isValid ? '' : '::InvalidPhoneNumber';

    this.form().get('phoneNumber').setValue(isValid ? phoneNumber : '');
    phoneControl.markAsTouched();
    // Kept: the error text is derived from the control's touched/errors state, which is not a
    // signal, and intl-tel-input can call this outside a DOM event (e.g. on its initial value).
    this.cdr.markForCheck();
  }

  sendEmailConfirmation() {
    const baseUrl = `${window.location.protocol}//${window.location.host}/account/login`;
    const payload : SendEmailConfirmationRequest = {
      email: this.currentUser().email,
      userId: this.currentUser().id,
      appName:'MVC',
      returnUrl: baseUrl
    }
    this.proxyService.sendEmailConfirmation(payload).subscribe({
      next: () => {
        this.toasterService.success('::SuccessfullySendEmailConfirmation', '', { closable: false });
        this.fetchCurrentUser();
      }
    });
  }

  async sendPhoneConfirmation() {
    const payload : SendPhoneConfirmationRequest = {
      phoneNumber: this.currentUser().phoneNumber,
      userId: this.currentUser().id,
    }
    
    const results = await firstValueFrom(this.proxyService.sendPhoneConfirmation(payload));
    this.toasterService.success('::SuccessfullySendPhoneConfirmation', '', { closable: false });
    this.verificationCodeValidity.set(results.verificationCodeValidity);
    this.isConfirmationVisible.set(true);
  }
  
  closeConfiramtionPopup(){
    this.isConfirmationVisible.set(false);
    this.fetchCurrentUser();
  }
}
